import React, { useState, useMemo } from "react";
import { Container, Row, Col, Button } from "react-bootstrap";
import Loading from "../Loading";
import BlogCard from "./BlogCard";
import Particle from "../Particle";
import { useTranslation } from "react-i18next";
import { useData } from "../../context/DataContext";
import { getImageURL } from "../../api";

function Blog() {
  const { blogs, loading } = useData();
  const { t, i18n } = useTranslation();
  const lang = i18n.language.split("-")[0];
  const [selectedMonth, setSelectedMonth] = useState("all");

  // Get unique months from blogs
  const availableMonths = useMemo(() => {
    const monthNames = {
      uz: [
        "Yanvar",
        "Fevral",
        "Mart",
        "Aprel",
        "May",
        "Iyun",
        "Iyul",
        "Avgust",
        "Sentabr",
        "Oktabr",
        "Noyabr",
        "Dekabr",
      ],
      en: [
        "January",
        "February",
        "March",
        "April",
        "May",
        "June",
        "July",
        "August",
        "September",
        "October",
        "November",
        "December",
      ],
    };

    const months = blogs.map((blog) => {
      const date = new Date(blog.createdAt);
      const monthIndex = date.getMonth();
      const year = date.getFullYear();

      return {
        value: `${year}-${String(monthIndex + 1).padStart(2, "0")}`,
        label: `${monthNames[lang] ? monthNames[lang][monthIndex] : monthNames.en[monthIndex]} ${year}`,
      };
    });

    // Remove duplicates
    const uniqueMonths = [];
    const seen = new Set();
    months.forEach((m) => {
      if (!seen.has(m.value)) {
        seen.add(m.value);
        uniqueMonths.push(m);
      }
    });

    return uniqueMonths.sort((a, b) => b.value.localeCompare(a.value));
  }, [blogs, lang]);

  // Filter blogs by selected month
  const filteredBlogs = useMemo(() => {
    if (selectedMonth === "all") return blogs;

    return blogs.filter((blog) => {
      const date = new Date(blog.createdAt);
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
      return monthKey === selectedMonth;
    });
  }, [blogs, selectedMonth]);

  if (loading) {
    return <Loading message="Loading Blogs..." />;
  }

  return (
    <Container fluid className="project-section">
      <Particle />
      <Container>
        <h1 className="project-heading">
          {t("my_recent", "My Recent")}{" "}
          <strong className="purple">{t("blogs", "Blogs")} </strong>
        </h1>
        <p style={{ color: "white" }}>
          {t("blog_subtitle", "Here are a few blogs I've written recently.")}
        </p>

        {/* Month Filter */}
        {availableMonths.length > 0 && (
          <Row className="justify-content-center mb-4">
            <Col md={10} className="text-center">
              <div className="d-flex flex-wrap justify-content-center gap-2">
                <Button
                  variant={
                    selectedMonth === "all" ? "primary" : "outline-primary"
                  }
                  onClick={() => setSelectedMonth("all")}
                  style={{
                    background:
                      selectedMonth === "all"
                        ? "linear-gradient(135deg, #c770f0 0%, #be50f4 100%)"
                        : "transparent",
                    border: "2px solid #c770f0",
                    color: selectedMonth === "all" ? "#fff" : "#c770f0",
                    fontWeight: "600",
                    borderRadius: "8px",
                    padding: "8px 20px",
                    margin: "2px",
                    fontSize: "14px",
                  }}
                >
                  {lang === "uz" ? "Barchasi" : "All"}
                </Button>
                {availableMonths.map((month) => (
                  <Button
                    key={month.value}
                    variant={
                      selectedMonth === month.value
                        ? "primary"
                        : "outline-primary"
                    }
                    onClick={() => setSelectedMonth(month.value)}
                    style={{
                      background:
                        selectedMonth === month.value
                          ? "linear-gradient(135deg, #c770f0 0%, #be50f4 100%)"
                          : "transparent",
                      border: "2px solid #c770f0",
                      color: selectedMonth === month.value ? "#fff" : "#c770f0",
                      fontWeight: "600",
                      borderRadius: "8px",
                      padding: "8px 20px",
                      margin: "2px",
                      fontSize: "14px",
                    }}
                  >
                    {month.label}
                  </Button>
                ))}
              </div>
            </Col>
          </Row>
        )}

        <Row style={{ justifyContent: "center", paddingBottom: "10px" }}>
          {filteredBlogs.map((blog) => (
            <Col md={4} className="project-card" key={blog._id}>
              <BlogCard
                imgPath={
                  getImageURL(blog.coverImage) ||
                  "https://via.placeholder.com/300x200"
                }
                title={blog.title[lang]}
                description={
                  blog.content[lang]
                    .replace(/<[^>]*>?/gm, "")
                    .substring(0, 150) + "..."
                }
                link={`/blog/${blog.slug}`}
              />
            </Col>
          ))}
          {filteredBlogs.length === 0 && (
            <p style={{ color: "white" }}>
              {selectedMonth === "all"
                ? t(
                    "no_blogs",
                    "No blogs found yet. Manage them in Admin Panel."
                  )
                : lang === "uz"
                  ? "Bu oyda blog topilmadi"
                  : "No blogs found for this month"}
            </p>
          )}
        </Row>
      </Container>
    </Container>
  );
}

export default Blog;
